import { useReactiveVar } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { mdiSchool } from '@mdi/js';

import { Icon } from 'common/components/Icon';
import { IScormModuleListItemFragment } from 'graphql/types';
import { TTrainingModuleProps } from 'training/types';
import { ModuleConditionsChips } from 'training/components/ModuleConditionsChips';
import {
    ListItem,
    ListItemMedia,
    ListItemText,
} from 'common/components/ListItem';
import { Link } from 'common/components/Link';
import { trainingStudentView } from 'training/state/trainingStudentView';
import { getScormUrl } from 'training/utils/scorm';
import { ModuleSecondaryAction } from 'training/components/ModuleSecondaryAction';

export const ScormModule = ({
    title,
    description,
    scorm,
    disabled,
    conditions,
    alwaysOpen,
    startDate,
    conditionEffect,
    groupConditionEffect,
    canUpdate,
    id,
    trainingId,
    image,
    portfolioItem,
    hasProgress,
    weight,
}: TTrainingModuleProps<IScormModuleListItemFragment>) => {
    const [translate] = useTranslation();
    const studentView = useReactiveVar(trainingStudentView);

    const thumbnail = image?.url || scorm.thumbnail?.url || undefined;

    const itemContent = (
        <>
            <ListItemMedia color="primary" image={thumbnail}>
                <Icon path={mdiSchool} size="1.8rem" sizeSm="3rem" />
            </ListItemMedia>
            <ListItemText
                primary={title || scorm.title}
                secondary={description}
            >
                <ModuleConditionsChips
                    alwaysOpen={alwaysOpen}
                    conditionEffect={conditionEffect}
                    conditions={conditions}
                    groupConditionEffect={groupConditionEffect}
                    startDate={startDate}
                />
            </ListItemText>
        </>
    );

    if (disabled) {
        return <ListItem disabled>{itemContent}</ListItem>;
    }

    if (!studentView && canUpdate) {
        return (
            <ListItem
                button
                component={Link}
                params={{
                    trainingId,
                    moduleId: id,
                    type: 'scorm',
                }}
                to="TRAINING_UPDATE_MODULE"
            >
                {itemContent}

                <ModuleSecondaryAction
                    forUpdate
                    hasProgress={!!hasProgress}
                    portfolioItem={portfolioItem}
                    weight={weight}
                />
            </ListItem>
        );
    }

    return (
        <ListItem button component={Link} href={getScormUrl(scorm.id)}>
            {itemContent}

            <ModuleSecondaryAction
                actionText={translate('start')}
                hasProgress={!!hasProgress}
                portfolioItem={portfolioItem}
                weight={weight}
            />
        </ListItem>
    );
};
